import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { Layout, Seo, PageHero } from '@/components/generals'
import { routes } from '@/lib/routes'

const PricingPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Pricing' description='Flexible pricing for companies and developers. Simple plans that scale with your team.' />

            <PageHero
                badge='New'
                eyebrow='Introducing the Class app'
                eyebrowHref='/features'
                title={
                    <>
                        Flexible pricing
                        <br />
                        for <span className='text-gradient'>companies</span> and developers
                    </>
                }
                description='Start free and upgrade as you grow. Every plan keeps your time, expenses, and tasks in one place.'
                showButtons
            />

            <div id='pricing' className='section animation-on-page-load'>
                <div className='container'>
                    <div className='pricing-table'>
                        <div className='content'>
                            <div className='pricing-table-info'>
                                <div className='fade-in-on-scroll'>
                                    <h2 className='heading-3-style'>Keep everything organised</h2>
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <div>
                                        Choose the plan that fits your team. Every tier brings your time, expenses, and tasks into one
                                        calm, shared workspace, with no setup headaches and no surprises.
                                    </div>
                                </div>
                                <div className='fade-in-on-scroll'>
                                    <div className='button-holder-left-justify-bottom'>
                                        <Link href={routes.contact} className='button black-button-with-icon w-button'>
                                            Talk to sales
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='content'>
                            <div className='card-item pricing-card'>
                                <div className='card-item-content pricing-item-content'>
                                    <div className='pricing-item-content'>
                                        <div className='pricing-title'>Core</div>
                                        <div className='pricing-main-content'>
                                            <div className='product-item-price'>
                                                <sup className='superscript'>$</sup>20
                                            </div>
                                            <div className='pricing-item-price-desc'>
                                                per user / month
                                                <br />
                                                billed annually
                                            </div>
                                        </div>
                                        <div className='pricing-item-description'>For small teams that want a world-class experience without all the admin.</div>
                                    </div>
                                    <div className='pricing-item-button-holder'>
                                        <Link href={routes.contact} className='button w-button'>
                                            Try for Free
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='content'>
                            <div className='card-item pricing-card'>
                                <div className='card-item-content pricing-item-content'>
                                    <div className='pricing-item-content'>
                                        <div className='pricing-title'>Pro</div>
                                        <div className='pricing-main-content'>
                                            <div className='product-item-price'>
                                                <sup className='superscript'>$</sup>50
                                            </div>
                                            <div className='pricing-item-price-desc'>
                                                per user / month
                                                <br />
                                                billed annually
                                            </div>
                                        </div>
                                        <div className='pricing-item-description'>For growing teams that need advanced controls, integrations, and priority support.</div>
                                    </div>
                                    <div className='pricing-item-button-holder'>
                                        <Link href={routes.contact} className='button w-button'>
                                            Try for Free
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='container'>
                    <div className='text-center mobile-left'>
                        <div className='fade-in-on-scroll'>
                            <div className='heading-container'>
                                <h2 className='heading-3-style'>All plans include:</h2>
                            </div>
                        </div>
                    </div>
                    <div className='w-layout-grid grid-6-col'>
                        <div className='content'>
                            <div className='checklist-richtext w-richtext'>
                                <ul role='list'>
                                    <li>
                                        Get started with <strong>San Diego</strong>
                                    </li>
                                    <li>
                                        High <strong>quality</strong> assets
                                    </li>
                                    <li>
                                        <strong>Unlimited</strong> access
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className='content'>
                            <div className='checklist-richtext w-richtext'>
                                <ul role='list'>
                                    <li>
                                        Full storage <strong>security</strong>
                                    </li>
                                    <li>
                                        <strong>+3 team</strong> members
                                    </li>
                                    <li>
                                        <strong>AI &amp; ML</strong> services
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className='content'>
                            <div className='checklist-richtext w-richtext'>
                                <ul role='list'>
                                    <li>
                                        <strong>24/7</strong> support
                                    </li>
                                    <li>
                                        Direct <strong>chat</strong>
                                    </li>
                                    <li>
                                        <strong>Emails</strong> and <strong>Phone</strong> calls
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default PricingPage
